// ./src/hooks/useRecurringEvents.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  RecurringEvent,
  RecurringEventService,
  RecurringEventFilter,
  RecurringEventInput,
  Pagination,
  SortItem,
} from "@/utils";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseRecurringEventsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<RecurringEventFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseRecurringEventsReturn {
  // Data state
  recurringEvent: RecurringEvent | null;
  recurringEvents: RecurringEvent[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<RecurringEventFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingRecurringEventId: string | null;
  selectedRecurringEvent: RecurringEvent | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<RecurringEventFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<RecurringEvent[]>;
  create: (params: RecurringEventInput) => Promise<RecurringEvent | null>;
  update: (
    id: string,
    params: RecurringEventInput
  ) => Promise<RecurringEvent | null>;
  remove: (id: string) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<RecurringEventFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedRecurringEvent: (recurringEvent: RecurringEvent | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  recurringEventsByPattern: Record<string, RecurringEvent[]>;
  recurringEventsByFrequency: Record<string, RecurringEvent[]>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [
  { key: "recurringEvents.name", direction: "asc" },
];

export const useRecurringEvents = (
  options: UseRecurringEventsOptions = {}
): UseRecurringEventsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [recurringEvent, setRecurringEvent] = useState<RecurringEvent | null>(
    null
  );
  const [recurringEvents, setRecurringEvents] = useState<RecurringEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<RecurringEventFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingRecurringEventId, setRemovingRecurringEventId] = useState<
    string | null
  >(null);
  const [selectedRecurringEvent, setSelectedRecurringEvent] =
    useState<RecurringEvent | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => recurringEvents.length === 0 && !loading,
    [recurringEvents.length, loading]
  );

  const recurringEventsByPattern = useMemo(() => {
    return recurringEvents.reduce((acc, event) => {
      const pattern = event.recurrencePattern || "NONE";
      if (!acc[pattern]) {
        acc[pattern] = [];
      }
      acc[pattern].push(event);
      return acc;
    }, {} as Record<string, RecurringEvent[]>);
  }, [recurringEvents]);

  const recurringEventsByFrequency = useMemo(() => {
    return recurringEvents.reduce((acc, event) => {
      const frequency = event.recurrencePattern || "NONE";
      if (!acc[frequency]) {
        acc[frequency] = [];
      }
      acc[frequency].push(event);
      return acc;
    }, {} as Record<string, RecurringEvent[]>);
  }, [recurringEvents]);

  // Helper function to update recurring event in list if it exists, or add it if not
  const updateOrAddRecurringEventInList = useCallback(
    (updatedEvent: RecurringEvent) => {
      setRecurringEvents((prev) => {
        const existingIndex = prev.findIndex((re) => re.id === updatedEvent.id);
        if (existingIndex >= 0) {
          const newEvents = [...prev];
          newEvents[existingIndex] = updatedEvent;
          return newEvents;
        } else {
          return [updatedEvent, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove recurring event from list if it exists
  const removeRecurringEventFromList = useCallback((eventId: string) => {
    setRecurringEvents((prev) => prev.filter((re) => re.id !== eventId));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setRecurringEvent(null);
    setRecurringEvents([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingRecurringEventId(null);
    setSelectedRecurringEvent(null);
  }, []);

  // Get single recurring event (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await RecurringEventService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const eventData = result.data[0] as RecurringEvent;
          setRecurringEvent(eventData);
          updateOrAddRecurringEventInList(eventData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load recurring event";
          setLastError(errorMessage);
          setRecurringEvent(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load recurring event";
        setLastError(errorMessage);
        setRecurringEvent(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRecurringEventInList]
  );

  // List recurring events (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<RecurringEventFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setRecurringEvents([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams: SortItem[] = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await RecurringEventService.list(
          filter as RecurringEventFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEvents = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setRecurringEvents((prev) => {
              const existingIds = new Set(prev.map((re) => re.id));
              const uniqueNewEvents = newEvents.filter(
                (re) => !existingIds.has(re.id)
              );
              return [...prev, ...uniqueNewEvents];
            });
          } else {
            setRecurringEvents(newEvents);
            // Auto-select first event if none selected and we have data
            if (reset && newEvents.length > 0) {
              setSelectedRecurringEvent((prev) => (prev ? prev : newEvents[0]));
            }
          }

          setHasMore(newEvents.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load recurring events";
          setLastError(errorMessage);
          if (!isAppending) setRecurringEvents([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load recurring events";
        setLastError(errorMessage);
        if (!isAppending) setRecurringEvents([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many recurring events by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<RecurringEvent[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await RecurringEventService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedEvents = Array.isArray(result.data) ? result.data : [];
          loadedEvents.forEach((event) => {
            updateOrAddRecurringEventInList(event);
          });
          setLastError(null);
          return loadedEvents;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to load recurring events";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load recurring events";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddRecurringEventInList]
  );

  // Create recurring event (matching API method name)
  const create = useCallback(
    async (params: RecurringEventInput): Promise<RecurringEvent | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await RecurringEventService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newEvent = result.data[0] as RecurringEvent;
          setRecurringEvent(newEvent);
          setRecurringEvents((prev) => [newEvent, ...prev]);
          setLastError(null);
          return newEvent;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to create recurring event";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to create recurring event";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update recurring event (matching API method name)
  const update = useCallback(
    async (
      id: string,
      params: RecurringEventInput
    ): Promise<RecurringEvent | null> => {
      if (!id) {
        setLastError("Recurring event ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await RecurringEventService.update(id, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedEvent = result.data[0] as RecurringEvent;
          setRecurringEvent(updatedEvent);
          updateOrAddRecurringEventInList(updatedEvent);
          setLastError(null);
          return updatedEvent;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to update recurring event";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update recurring event";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddRecurringEventInList]
  );

  // Remove recurring event (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Recurring event ID is required for removal");
        return false;
      }

      setRemovingRecurringEventId(id);
      setLastError(null);

      try {
        const result = await RecurringEventService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setRecurringEvent(null);
          removeRecurringEventFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] ||
            "Failed to remove recurring event";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove recurring event";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingRecurringEventId(null);
      }
    },
    [removeRecurringEventFromList]
  );

  // Load more recurring events
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh recurring events
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    recurringEvent,
    recurringEvents,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingRecurringEventId,
    selectedRecurringEvent,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedRecurringEvent,
    reset,
    recurringEventsByPattern,
    recurringEventsByFrequency,
    lastError,
    clearError,
  };
};
